import { useState, useRef, useCallback, useEffect, Suspense, type ReactNode } from 'react'
import {
  FolderOpen,
  Landmark,
  Store,
  Clock3,
  ShoppingBasket,
  ClipboardPen,
  Contact,
  ChevronDown,
  Search,
  BarChart3,
  Lightbulb,
  X,
} from 'lucide-react'
import type { User } from '@/api/types'
import { PANEL_COMPONENTS } from '@/components/command-centre/panel-registry'
import { TAB_ROUTES } from '@/components/command-centre/navigation-map'
import { cn } from '@/lib/utils'
import { TransitionNudge } from '@/components/command-centre/TransitionNudge'
import { WelcomeBanner } from '@/components/command-centre/WelcomeBanner'
import { FinderBar } from '@/components/command-centre/FinderBar'
import { WindowManagerProvider } from '@/components/command-centre/WindowManager'
import { useWindowManager } from '@/components/command-centre/use-window-manager'
import { VirtualWindow } from '@/components/command-centre/VirtualWindow'
import { resolveWindowComponent } from '@/components/command-centre/window-routes'
import { isDesktop } from '@/lib/platform'
import { Spinner } from '@/components/ui/spinner'
import { UpdateBanner } from '@/components/common/UpdateBanner'

interface CommandCentreLayoutProps {
  children: ReactNode
  user?: User | null
  orgName?: string
  features?: Record<string, boolean> | null
  graduatedTabs?: string[]
  showNudge?: boolean
  daysRemaining?: number | null
  expiryDate?: string | null
  declinedCount?: number
  showWelcome?: boolean
  classicModeSource?: string | null
  onAcceptTransition?: () => void
  onDeclineTransition?: () => void
  onDismissWelcome?: () => void
  onSwitchToModern?: () => void
  onLogout?: () => void
}

const CC_TABS: ReadonlyArray<{ id: string; label: string; icon: typeof FolderOpen; feature?: string }> = [
  { id: 'accounts', label: 'Accounts', icon: FolderOpen },
  { id: 'banking', label: 'Banking', icon: Landmark, feature: 'banking' },
  { id: 'sales', label: 'Sales', icon: Store, feature: 'invoicing' },
  { id: 'time-billing', label: 'Time Billing', icon: Clock3, feature: 'timesheets' },
  { id: 'purchases', label: 'Purchases', icon: ShoppingBasket, feature: 'purchase_orders' },
  { id: 'inventory', label: 'Inventory', icon: ClipboardPen, feature: 'inventory' },
  { id: 'card-file', label: 'Card File', icon: Contact },
]

const BOTTOM_TOOLS = [
  { id: 'find', label: 'Find Transactions', icon: Search, route: '/journal-entries' },
  { id: 'reports', label: 'Reports', icon: BarChart3, route: '/reports' },
  { id: 'insights', label: 'Business Insights', icon: Lightbulb, route: '/reports' },
] as const

function CommandCentreInner({
  children,
  user,
  orgName = 'My Organisation',
  features,
  graduatedTabs = [],
  showNudge = false,
  daysRemaining = null,
  expiryDate = null,
  declinedCount = 0,
  showWelcome = false,
  classicModeSource = null,
  onAcceptTransition,
  onDeclineTransition,
  onDismissWelcome,
  onSwitchToModern,
  onLogout,
}: CommandCentreLayoutProps) {
  const wm = useWindowManager()
  const [activeTab, setActiveTab] = useState<string>('accounts')
  const [openDropdown, setOpenDropdown] = useState<string | null>(null)
  const [nudgeDismissed, setNudgeDismissed] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const desktop = isDesktop()

  // Auto-create the pinned CC window on mount
  const ccTitle = orgName + ' - Command Centre'
  useEffect(() => {
    wm.openWindow('__cc__', ccTitle, {
      pinned: true,
      size: { w: 704, h: 678 },
      position: { x: 20, y: 10 },
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleTabClick = (tabId: string) => {
    if (graduatedTabs.includes(tabId)) {
      // Graduated tabs open directly in a VirtualWindow - eases users into modern UI
      const route = TAB_ROUTES[tabId] || '/'
      const title = CC_TABS.find(t => t.id === tabId)?.label || 'Page'
      wm.openWindow(route, title)
    } else {
      setActiveTab(tabId)
    }
  }

  const handleOpenWindow = useCallback((route: string, title: string) => {
    wm.openWindow(route, title)
  }, [wm])

  const handleBottomTool = (route: string, title: string) => {
    setOpenDropdown(null)
    wm.openWindow(route, title)
  }

  // Measure the full desktop area for window constraints
  const getContainerSize = useCallback(() => {
    if (containerRef.current) {
      return {
        w: containerRef.current.clientWidth,
        h: containerRef.current.clientHeight,
      }
    }
    return { w: 800, h: 600 }
  }, [])

  const containerSize = getContainerSize()

  /* ── CC window content (rendered inside the pinned VirtualWindow) ── */
  const ccContent = (
    <div className="flex h-full flex-col overflow-hidden bg-background">
      {/* Org name */}
      <div className="px-4 pt-3 pb-1">
        <span className="text-sm font-semibold text-foreground">{orgName}</span>
      </div>

      {/* Tab bar with icons */}
      <div className="flex items-end justify-center gap-1 px-2 pb-0.5">
        {CC_TABS.filter(tab => {
          if (tab.feature && features && features[tab.feature] === false) return false
          return true
        }).map(tab => {
          const isActive = activeTab === tab.id
          const isGraduated = graduatedTabs.includes(tab.id)
          return (
            <button
              key={tab.id}
              onClick={() => handleTabClick(tab.id)}
              className={cn(
                'flex flex-col items-center gap-0.5 px-3 py-1.5 text-[10px] font-medium transition-colors rounded-t',
                isActive && !isGraduated && 'text-primary border-b-2 border-b-primary',
                isGraduated && 'text-muted-foreground opacity-60',
                !isActive && !isGraduated && 'text-foreground hover:text-primary',
              )}
            >
              <tab.icon className="h-6 w-6" />
              <span>{tab.label}</span>
              {isGraduated && (
                <span className="rounded bg-primary/20 px-1 text-[8px] font-semibold text-primary">migrated</span>
              )}
            </button>
          )
        })}
      </div>

      {/* Purple header bar with tab name */}
      <div className="mx-3 flex items-center justify-between rounded-t bg-primary px-3 py-1.5">
        <span className="text-xs font-semibold text-primary-foreground">
          {CC_TABS.find(t => t.id === activeTab)?.label}
        </span>
      </div>

      {/* Welcome banner */}
      {showWelcome && onDismissWelcome && (
        <WelcomeBanner
          source={classicModeSource}
          expiryDate={expiryDate}
          onDismiss={onDismissWelcome}
        />
      )}

      {/* Flowchart content area */}
      <div className="mx-3 flex-1 overflow-auto border border-t-0 border-border bg-card rounded-b">
        {(() => {
          const Panel = PANEL_COMPONENTS[activeTab]
          return Panel ? <Panel /> : children
        })()}
      </div>

      {/* Bottom tools bar (inside CC window) */}
      <div className="relative mx-3 mb-2 mt-1 flex items-stretch rounded border border-border bg-card">
        {BOTTOM_TOOLS.map(tool => (
          <div key={tool.id} className="relative flex flex-1 items-stretch border-r border-border/50 last:border-r-0">
            <button
              onClick={() => setOpenDropdown(openDropdown === tool.id ? null : tool.id)}
              className="flex w-full items-center justify-center gap-1 py-1.5 text-[10px] text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
            >
              <span>{tool.label}</span>
              <ChevronDown className="h-2.5 w-2.5" />
            </button>
            {openDropdown === tool.id && (
              <div className="absolute bottom-full left-0 mb-1 w-40 rounded-md border border-border bg-popover p-1 shadow-md z-50">
                <button
                  onClick={() => handleBottomTool(tool.route, tool.label)}
                  className="flex w-full items-center gap-2 rounded px-3 py-2 text-xs text-popover-foreground hover:bg-accent transition-colors"
                >
                  <tool.icon className="h-3.5 w-3.5" />
                  Open {tool.label}
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  )

  return (
    <div className="relative flex h-screen flex-col overflow-hidden" style={{ background: 'hsl(0 0% 75%)' }}>
      <UpdateBanner />
      {/* Finder bar (menu bar) */}
      <FinderBar
        user={user}
        activeTab={activeTab}
        onSwitchTab={setActiveTab}
        onOpenWindow={handleOpenWindow}
        openWindowTitles={wm.windows.map(w => w.title)}
        onCloseAllWindows={wm.closeAll}
        onSwitchToModern={onSwitchToModern}
        onLogout={onLogout}
      />

      {/* Desktop area - all windows (CC + children) live here */}
      <div ref={containerRef} className="relative flex-1 overflow-hidden">
        {wm.windows.map(win => {
          const isCCWindow = win.route === '__cc__'
          const Component = isCCWindow ? null : resolveWindowComponent(win.route)

          // For non-CC windows, skip if no matching component
          if (!isCCWindow && !Component) return null

          return (
            <VirtualWindow
              key={win.id}
              window={win}
              onFocus={() => wm.focusWindow(win.id)}
              onClose={() => wm.closeWindow(win.id)}
              onMinimize={() => wm.minimizeWindow(win.id)}
              onMaximize={() => wm.maximizeWindow(win.id)}
              onMove={(pos) => wm.moveWindow(win.id, pos)}
              onResize={(size) => wm.resizeWindow(win.id, size)}
              containerSize={containerSize}
            >
              {isCCWindow ? ccContent : (
                <div className="modern-dark h-full overflow-auto p-4">
                  <Suspense fallback={<div className="flex h-full items-center justify-center"><Spinner size="md" /></div>}>
                    {Component && <Component />}
                  </Suspense>
                </div>
              )}
            </VirtualWindow>
          )
        })}

        {/* Max windows modal */}
        {wm.maxReached && (
          <div className="absolute inset-0 z-[200] flex items-center justify-center bg-black/30">
            <div className="mx-4 w-full max-w-sm rounded-lg border border-border bg-card p-5 shadow-xl">
              <h3 className="text-sm font-semibold text-foreground">Too many windows open</h3>
              <p className="mt-1 text-xs text-muted-foreground">
                Close a window to open another. Maximum 4 windows.
              </p>
              <div className="mt-3 space-y-1">
                {wm.windows.filter(w => !w.pinned).map(w => (
                  <div key={w.id} className="flex items-center justify-between rounded px-2 py-1.5 text-xs hover:bg-muted/50">
                    <span className="text-foreground">{w.title}</span>
                    <button
                      onClick={() => wm.closeWindow(w.id)}
                      className="rounded p-0.5 text-muted-foreground hover:text-destructive transition-colors"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Minimized window chips (taskbar at bottom of desktop) */}
        {!desktop && wm.windows.filter(w => w.minimized).length > 0 && (
          <div className="absolute bottom-0 left-0 right-0 flex items-center gap-1 bg-card/80 border-t border-border px-2 py-1 z-[100]">
            {wm.windows.filter(w => w.minimized).map(w => (
              <button
                key={w.id}
                onClick={() => wm.restoreWindow(w.id)}
                className="rounded border border-border/50 bg-muted/50 px-2 py-0.5 text-[10px] font-medium text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
              >
                {w.title}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Click-away for dropdowns */}
      {openDropdown && (
        <div className="fixed inset-0 z-40" onClick={() => setOpenDropdown(null)} />
      )}

      {/* Transition nudge */}
      {showNudge && !nudgeDismissed && onAcceptTransition && onDeclineTransition && (
        <TransitionNudge
          daysRemaining={daysRemaining}
          expiryDate={expiryDate}
          declinedCount={declinedCount}
          onAccept={onAcceptTransition}
          onDecline={() => {
            setNudgeDismissed(true)
            onDeclineTransition()
          }}
        />
      )}
    </div>
  )
}

export function CommandCentreLayout(props: CommandCentreLayoutProps) {
  return (
    <WindowManagerProvider>
      <CommandCentreInner {...props} />
    </WindowManagerProvider>
  )
}
